import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Camera, Utensils, Droplets, Dumbbell, Scale, Target, BarChart3, ShieldCheck, Smartphone, Lock, UserPlus, ListChecks, TrendingUp, AlertTriangle, MapPin, ChevronLeft, ChevronRight } from "lucide-react";
import { PulseLayout, usePulseMeta, C } from "./PulseLayout";
import { ScreenLightbox, LaunchSignup, StoreBadges } from "./PulseExtras";

const S = "/pulse-assets/screens/";
const SCREENS = [
  { id: "today", label: "Today dashboard", src: `${S}01-today-dashboard.webp`, alt: "PULSE Today dashboard with remaining calories ring, goal, food and exercise totals, protein, carbs and fat rings, a Scan a meal shortcut and hydration progress" },
  { id: "scanner", label: "AI food scan", group: "ai", src: `${S}02-ai-food-scanner.webp`, alt: "PULSE AI food scan camera view framing a bowl of loaded sweet potato fries, with gallery, capture and manual entry buttons and a note that results are estimates" },
  { id: "results", label: "AI scan result", group: "ai", src: `${S}03-ai-food-results.webp`, alt: "PULSE scan result estimating a loaded sweet potato fries bowl at 720 kcal with protein, carbs and fat, a probable match label, portion controls and Retake or Save to diary buttons" },
  { id: "diary", label: "Food diary", src: `${S}04-food-diary.webp`, alt: "PULSE food diary with total intake against target, macro bars and breakfast and lunch entries with Scan and Add food buttons" },
  { id: "train", label: "Exercise library", src: `${S}05-train-library.webp`, alt: "PULSE Train screen with today's workout summary, exercise search and filters by muscle, equipment and level, listing chest exercises" },
  { id: "exercise", label: "Exercise guide", src: `${S}06-exercise-details.webp`, alt: "PULSE exercise detail for Alternating Floor Press with start and end photos, a quick guide, level, equipment, target muscles and step-by-step instructions" },
  { id: "hydration", label: "Hydration", src: `${S}07-hydration-tracker.webp`, alt: "PULSE hydration tracker showing 1.75 of 2.5 litres with quick-add amounts, a daily target selector and the last seven days" },
  { id: "stats", label: "Stats & progress", src: `${S}08-stats-progress.webp`, alt: "PULSE seven-day stats with average intake, average burn, days logged, net average, a calorie intake chart against goal and macros versus target" },
  { id: "activity", label: "Today's activity", demo: true, src: `${S}10-today-activity-detail.webp`, alt: "PULSE Today screen showing steps, distance, active calories and exercise minutes estimated from logged workouts, a logged walking workout and recently logged meals" },
];

const FEATURES = [
  { icon: Camera, title: "AI-assisted food recognition", text: "Snap or choose a photo of your meal for an AI-assisted estimate of the food, calories and macros. Check every result before logging, and retake the photo or add the food manually if it looks wrong." },
  { icon: Utensils, title: "Nutrition & macro tracking", text: "Log foods manually by meal type and track calories, protein, carbohydrates and fat against your daily targets." },
  { icon: Dumbbell, title: "Exercise & activity logging", text: "Record activities and strength sessions with duration, sets, reps and weight, browse the exercise library, save favourites and see personal bests." },
  { icon: Droplets, title: "Water intake", text: "Log your water through the day and watch your progress towards your hydration goal." },
  { icon: Scale, title: "Body-weight progress", text: "Record your weight over time and follow your progress towards a target weight." },
  { icon: Target, title: "Personal targets", text: "Set daily goals for calories, protein, carbohydrates, fat, water, steps and active calories." },
  { icon: BarChart3, title: "Progress statistics", text: "A Today dashboard with activity rings, plus reports that bring your nutrition and training together." },
];

const STEPS = [
  { icon: UserPlus, title: "Create your account", text: "Sign up with email or Google and set your fitness and nutrition goals." },
  { icon: ListChecks, title: "Track your day", text: "Log meals, workouts, water and daily activity in a few taps." },
  { icon: TrendingUp, title: "Review and adjust", text: "See your progress over time and fine-tune your routine." },
];

const rise = (i) => ({ animation: `pulseRise .7s cubic-bezier(.2,.7,.2,1) ${i * 90}ms both` });

const Eyebrow = ({ children }) => <p className="text-xs tracking-[0.3em] uppercase mb-3" style={{ color: C.teal }}>{children}</p>;

const Phone = ({ screen, className = "", src, eager }) => (
  <div className={`relative rounded-[2.4rem] border p-2.5 pt-6 ${className}`} style={{ borderColor: C.slate, background: C.ocean, boxShadow: "0 40px 100px -40px rgba(85,228,231,0.4)" }} data-testid={`pulse-screen-${screen.id}${eager ? "-hero" : ""}`}>
    <div className="absolute top-2.5 left-1/2 -translate-x-1/2 h-1.5 w-14 rounded-full z-10" style={{ background: C.slate }} aria-hidden="true" />
    <div className="aspect-[9/19.5] overflow-hidden rounded-[1.9rem]" style={{ background: C.navy }}>
      <img src={src || screen.src} alt={screen.alt} width="720" height="1558" className="w-full h-full object-cover object-top" loading={eager ? "eager" : "lazy"} decoding="async" />
    </div>
  </div>
);

const Hero = () => {
  return (
    <section className="relative overflow-hidden" data-testid="pulse-hero">
      <div className="absolute -top-48 -right-32 w-[560px] h-[560px] rounded-full blur-3xl opacity-20 pointer-events-none" style={{ background: C.teal }} />
      <div className="absolute top-1/2 -left-48 w-[420px] h-[420px] rounded-full blur-3xl opacity-10 pointer-events-none" style={{ background: C.cyan }} />
      <div className="relative max-w-6xl mx-auto px-5 pt-14 pb-20 md:pt-24 grid lg:grid-cols-[1.2fr_1fr] gap-14 items-center">
        <div>
          <div className="flex items-center gap-4 mb-7" style={rise(0)}>
            <img src="/pulse-assets/pulse-icon-512.png" alt="PULSE by Veyronix logo" width="64" height="64" className="rounded-2xl shadow-lg" data-testid="pulse-hero-logo" />
            <div>
              <p className="font-heading font-bold tracking-[0.18em] text-lg" data-testid="pulse-hero-product">PULSE by Veyronix</p>
              <p className="text-xs" style={{ color: C.silver }}>A product of Veyronix Limited</p>
            </div>
          </div>
          <span className="inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs tracking-widest uppercase mb-6" style={{ borderColor: C.slate, color: C.cyan, ...rise(1) }} data-testid="pulse-status-badge">
            <span className="h-1.5 w-1.5 rounded-full animate-pulse" style={{ background: C.cyan }} />Coming soon · UK first
          </span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] mb-6" style={rise(2)} data-testid="pulse-hero-title">
            Your Fitness.<br />Your Nutrition.<br /><span style={{ color: C.cyan }}>Your Progress.</span>
          </h1>
          <p className="text-base md:text-lg max-w-xl mb-8" style={{ color: C.silver, ...rise(3) }} data-testid="pulse-hero-text">
            Meet PULSE, your smarter fitness companion for tracking nutrition, logging workouts, monitoring progress and building healthier routines.
          </p>
          <div className="flex flex-wrap items-center gap-5" style={rise(4)}>
            <button type="button" disabled aria-disabled="true" className="rounded-full px-8 py-3.5 font-semibold cursor-not-allowed" style={{ background: C.cyan, color: C.navy }} data-testid="pulse-cta-coming-soon">Coming Soon</button>
            <StoreBadges />
            <a href="#preview" className="text-sm font-semibold underline-offset-4 hover:underline" style={{ color: C.white }} data-testid="pulse-hero-preview-link">See the app →</a>
          </div>
          <p className="mt-7 text-xs flex items-center gap-2" style={{ color: C.silver, ...rise(5) }}><Smartphone className="h-4 w-4 shrink-0" />Planned for iOS, Android and the web. For adults aged 18 and over.</p>
        </div>
        <div className="relative w-[250px] sm:w-[280px] mx-auto" style={rise(3)}>
          <Phone screen={SCREENS[0]} eager />
        </div>
      </div>
    </section>
  );
};

const Shot = ({ s, i, open, className = "" }) => (
  <button type="button" onClick={(e) => open(e, i)} aria-label={`View larger: ${s.label}`} className={`block rounded-[2.4rem] cursor-zoom-in focus:outline-none focus-visible:ring-2 focus-visible:ring-[#55E4E7] ${className}`} data-testid={`pulse-open-${s.id}`}>
    <Phone screen={s} className="transition-transform duration-300 hover:-translate-y-2" />
  </button>
);

const Caption = ({ n, children, tag }) => (
  <figcaption className="text-center text-sm mt-4" style={{ color: C.silver }}>
    {n}. {children}
    {tag && <span className="block mt-1.5 text-[11px] uppercase tracking-wider" style={{ color: C.cyan }}>{tag}</span>}
  </figcaption>
);

const Preview = () => {
  const ref = useRef(null);
  const [lb, setLb] = useState(null);
  const opener = useRef(null);
  useEffect(() => { if (ref.current) ref.current.scrollLeft = 0; }, []);
  const open = (e, i) => { opener.current = e.currentTarget; setLb(i); };
  const step = (dir) => { const el = ref.current; el.scrollBy({ left: dir * el.clientWidth * 0.8, behavior: "smooth" }); };
  const Arrow = ({ dir, Icon }) => (
    <button type="button" onClick={() => step(dir)} aria-label={dir < 0 ? "Previous screenshots" : "Next screenshots"} className="hidden md:inline-flex h-10 w-10 items-center justify-center rounded-full border transition-colors hover:bg-[#19354D]" style={{ borderColor: C.slate, color: C.cyan }} data-testid={`pulse-gallery-${dir < 0 ? "prev" : "next"}`}><Icon className="h-5 w-5" /></button>
  );
  const W = "snap-start shrink-0 w-[200px] sm:w-[220px]";
  const ai = SCREENS.filter((s) => s.group === "ai");
  let n = 0;
  return (
    <section id="preview" className="py-16 scroll-mt-20" data-testid="pulse-preview">
      <div className="max-w-6xl mx-auto px-5 flex items-end justify-between gap-4 mb-10">
        <div>
          <Eyebrow>Inside the app</Eyebrow>
          <h2 className="font-heading text-base md:text-lg font-semibold max-w-2xl">A closer look at PULSE</h2>
        </div>
        <div className="flex gap-3"><Arrow dir={-1} Icon={ChevronLeft} /><Arrow dir={1} Icon={ChevronRight} /></div>
      </div>
      <div className="max-w-6xl mx-auto">
        <div ref={ref} tabIndex={0} role="region" aria-label="PULSE app screenshots, scroll horizontally" className="flex items-start gap-6 overflow-x-auto snap-x snap-mandatory scroll-px-5 px-5 pt-3 pb-6 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#55E4E7] rounded-xl [scrollbar-width:thin]" data-testid="pulse-gallery">
          {SCREENS.map((s, i) => {
            if (s.group === "ai" && s !== ai[0]) return null;
            if (s.group === "ai") {
              const first = ++n; n += 1;
              return (
                <figure key="ai" className="snap-start shrink-0 rounded-[2.8rem] border p-3 sm:p-4" style={{ borderColor: C.slate, background: "rgba(85,228,231,0.04)", ...rise(i) }} data-testid="pulse-gallery-ai-group">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <Shot s={ai[0]} i={i} open={open} className="w-[176px] sm:w-[200px]" />
                    <ChevronRight className="h-6 w-6 shrink-0" style={{ color: C.cyan }} aria-hidden="true" />
                    <Shot s={ai[1]} i={i + 1} open={open} className="w-[176px] sm:w-[200px]" />
                  </div>
                  <Caption n={`${first}–${first + 1}`} tag="AI estimate · you confirm before saving">AI food scan → result</Caption>
                </figure>
              );
            }
            const num = ++n;
            const featured = i === 0;
            return (
              <figure key={s.id} className={featured ? "snap-start shrink-0 w-[240px] sm:w-[270px]" : W} style={rise(i)} data-testid={featured ? "pulse-gallery-featured" : undefined}>
                <Shot s={s} i={i} open={open} className="w-full" />
                <Caption n={num} tag={featured ? "Start here" : s.demo ? "Demonstration data" : undefined}>{s.label}</Caption>
              </figure>
            );
          })}
        </div>
        <p className="px-5 mt-2 text-xs" style={{ color: C.silver }} data-testid="pulse-gallery-note">Genuine PULSE screens shown with demonstration data. AI food results are estimates you can adjust before saving.</p>
      </div>
      <ScreenLightbox screens={SCREENS} index={lb} onChange={setLb} returnRef={opener} />
    </section>
  );
};

const Features = () => (
  <section className="max-w-6xl mx-auto px-5 py-16" data-testid="pulse-features">
    <Eyebrow>What PULSE does</Eyebrow>
    <h2 className="font-heading text-base md:text-lg font-semibold mb-10 max-w-2xl">Everyday tracking tools, designed to be quick to use and easy to understand.</h2>
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
      {FEATURES.map(({ icon: Icon, title, text }, i) => (
        <div key={title} className={`group rounded-2xl border p-6 transition-[transform,border-color] duration-300 hover:-translate-y-1 hover:border-[#3DB9BE] ${i === 0 ? "lg:col-span-2" : ""}`} style={{ borderColor: C.slate, background: i === 0 ? `linear-gradient(160deg, ${C.slate}, ${C.ocean})` : C.ocean }} data-testid={`pulse-feature-${title.toLowerCase().replace(/\W+/g, "-")}`}>
          <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl mb-4" style={{ background: "rgba(85,228,231,0.1)" }}><Icon className="h-5 w-5" style={{ color: C.cyan }} /></span>
          <h3 className={`font-heading font-semibold mb-2 ${i === 0 ? "text-xl" : ""}`}>{title}</h3>
          <p className={`leading-relaxed ${i === 0 ? "text-base" : "text-sm"}`} style={{ color: C.silver }}>{text}</p>
        </div>
      ))}
    </div>
  </section>
);

const HowItWorks = () => (
  <section className="max-w-6xl mx-auto px-5 py-16" data-testid="pulse-how-it-works">
    <Eyebrow>How PULSE works</Eyebrow>
    <h2 className="font-heading text-base md:text-lg font-semibold mb-10">Three simple steps</h2>
    <ol className="grid md:grid-cols-3 gap-5">
      {STEPS.map(({ icon: Icon, title, text }, i) => (
        <li key={title} className="relative rounded-2xl border p-6 overflow-hidden" style={{ borderColor: C.slate, background: C.ocean }} data-testid={`pulse-step-${i + 1}`}>
          <span className="absolute -right-2 -top-6 font-heading font-bold text-8xl opacity-[0.07]" aria-hidden="true">{i + 1}</span>
          <Icon className="h-6 w-6 mb-4" style={{ color: C.cyan }} />
          <h3 className="font-heading font-semibold mb-2">{i + 1}. {title}</h3>
          <p className="text-sm" style={{ color: C.silver }}>{text}</p>
        </li>
      ))}
    </ol>
  </section>
);

const Disclaimer = () => (
  <section className="max-w-6xl mx-auto px-5 py-8">
    <div className="rounded-2xl border-l-4 p-6 flex gap-4" style={{ borderColor: C.teal, background: C.ocean }} data-testid="pulse-medical-disclaimer">
      <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" style={{ color: C.teal }} />
      <div className="text-sm leading-relaxed" style={{ color: C.silver }}>
        <p className="font-semibold mb-1" style={{ color: C.white }}>Fitness and nutrition notice</p>
        <p>PULSE provides general fitness and nutrition tracking information. It does not provide medical diagnosis, treatment or advice. AI food estimates may be inaccurate and should not be relied on for medical decisions or food-allergy safety. Speak to a qualified healthcare professional before making significant changes to your diet or exercise routine.</p>
      </div>
    </div>
  </section>
);

const Privacy = () => (
  <section className="max-w-6xl mx-auto px-5 py-16">
    <div className="rounded-3xl border p-8 md:p-12 grid md:grid-cols-[auto_1fr] gap-8 items-start" style={{ borderColor: C.slate, background: `linear-gradient(135deg, ${C.ocean}, ${C.navy})` }} data-testid="pulse-privacy-summary">
      <ShieldCheck className="h-12 w-12" style={{ color: C.cyan }} />
      <div>
        <h2 className="font-heading text-base md:text-lg font-semibold mb-4">Your privacy choices</h2>
        <ul className="grid sm:grid-cols-2 gap-3 text-sm" style={{ color: C.silver }}>
          <li className="flex gap-2"><Lock className="h-4 w-4 mt-0.5 shrink-0" style={{ color: C.teal }} />Privacy settings in your profile, including your choice about optional usage analytics.</li>
          <li className="flex gap-2"><Lock className="h-4 w-4 mt-0.5 shrink-0" style={{ color: C.teal }} />Food photos are processed to estimate nutrition and are not saved by PULSE.</li>
          <li className="flex gap-2"><Lock className="h-4 w-4 mt-0.5 shrink-0" style={{ color: C.teal }} />No advertising SDKs and no third-party analytics tools.</li>
          <li className="flex gap-2"><Lock className="h-4 w-4 mt-0.5 shrink-0" style={{ color: C.teal }} />Sign in with email and password, or with Google.</li>
        </ul>
        <div className="flex flex-wrap gap-5 mt-6 text-sm">
          <Link to="/pulse/privacy-policy" className="font-semibold hover:underline" style={{ color: C.cyan }} data-testid="pulse-read-privacy">Read the Privacy Policy →</Link>
          <Link to="/pulse/terms" className="font-semibold hover:underline" style={{ color: C.cyan }} data-testid="pulse-read-terms">Read the Terms of Service →</Link>
          <Link to="/pulse/delete-account" className="font-semibold hover:underline" style={{ color: C.cyan }} data-testid="pulse-read-delete">Deleting your account →</Link>
        </div>
      </div>
    </div>
  </section>
);

const Availability = () => (
  <section className="max-w-6xl mx-auto px-5 py-10 text-center" data-testid="pulse-availability">
    <MapPin className="h-6 w-6 mx-auto mb-4" style={{ color: C.cyan }} />
    <p className="font-heading text-2xl sm:text-3xl font-bold mb-3">Coming Soon — Launching in the UK first.</p>
    <p className="text-sm mb-6" style={{ color: C.silver }}>Questions? Contact <a href="mailto:support@veyronix.co.uk" className="underline" style={{ color: C.cyan }}>support@veyronix.co.uk</a></p>
    <button type="button" disabled aria-disabled="true" className="rounded-full px-8 py-3 font-semibold cursor-not-allowed border" style={{ borderColor: C.cyan, color: C.cyan }} data-testid="pulse-availability-cta">Coming Soon</button>
    <StoreBadges className="justify-center mt-5" />
  </section>
);

export default function PulseLanding() {
  usePulseMeta("PULSE by Veyronix | Fitness & Nutrition Tracking App",
    "PULSE by Veyronix is a fitness and nutrition tracking app from Veyronix Limited: AI-assisted food scanning, nutrition, workouts, water and weight tracking. Coming soon, UK first.", "/pulse");
  return (
    <PulseLayout>
      <style>{"@keyframes pulseRise{from{opacity:0;transform:translateY(18px)}to{opacity:1;transform:none}}@media (prefers-reduced-motion:reduce){[style*=pulseRise]{animation:none!important}}"}</style>
      <Hero />
      <Preview />
      <Features />
      <HowItWorks />
      <Disclaimer />
      <Privacy />
      <LaunchSignup />
      <Availability />
    </PulseLayout>
  );
}
